import React from 'react'
import { useRouter } from 'next/router'
import { useFormContext } from 'react-hook-form'

import { Button } from '@wartek-id/button-v1'
import {
  Sheet,
  SheetContent,
  SheetCloseButton,
  SheetOverlay,
  SheetTitle,
} from '@wartek-id/sheet-v1'
import { Text } from '@wartek-id/text-v1'
import { Checkbox, CheckboxGroup } from '@wartek-id/checkbox-v1'

import useSearchFilters from 'app/SearchPage/hooks/useSearchFilters'
import useSearchQuery from 'app/SearchPage/hooks/useSearchQuery'
import { useContentSearchFilterOnboarding } from 'stores/content-search'
import useSETrackEvent from 'app/SearchPage/hooks/useSETrackEvent'
import {
  useContentSearchFilter,
  useContentSearchTracker,
} from 'stores/content-search'

const SearchFilterSheet = ({ isOpen, onClose }) => {
  const router = useRouter()
  const { clearErrors, setValue } = useFormContext()
  const [selectedFormats, setSelectedFormats] = React.useState<string[]>([])
  const [selectedTopics, setSelectedTopics] = React.useState<string[]>([])
  const hasAnyFilterSelected = React.useMemo(
    () => selectedFormats.length || selectedTopics.length,
    [selectedFormats.length, selectedTopics.length]
  )
  const { searchFiltersData } = useSearchFilters()
  const { searchQueryFromURL } = useSearchQuery()
  const contentSearchFilterOnboardingStore = useContentSearchFilterOnboarding()
  const contentSearchFilterStore = useContentSearchFilter()
  const contentSearchTrackerStore = useContentSearchTracker()
  const { SETrackSearchResultsPage } = useSETrackEvent()

  const submitFilterHandler = React.useCallback(() => {
    setValue('searchQuery', searchQueryFromURL)
    clearErrors('searchQuery')
    SETrackSearchResultsPage('CLICK_SEARCH_FILTER_RESULTS', {
      event_trigger: contentSearchTrackerStore.eventTrigger,
    })
    onClose()
    setTimeout(() => {
      if (!contentSearchFilterOnboardingStore.isFinished) {
        contentSearchFilterOnboardingStore.setFinished()
      }
      window.scrollTo({
        top: 0,
      })
    }, 500)

    router.replace({
      pathname: '/pencarian',
      query: {
        q: searchQueryFromURL,
        ...(selectedFormats.length ? { formats: selectedFormats } : {}),
        ...(selectedTopics.length ? { topics: selectedTopics } : {}),
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFormats, selectedTopics])

  const resetFilterHandler = React.useCallback(() => {
    setSelectedFormats([])
    setSelectedTopics([])
    SETrackSearchResultsPage('CLICK_SEARCH_FILTER_RESET', {
      event_trigger: contentSearchTrackerStore.eventTrigger,
    })
  }, [SETrackSearchResultsPage, contentSearchTrackerStore.eventTrigger])

  const clickOptionHandler = React.useCallback(
    (isChecked, key) => {
      if (isChecked) {
        SETrackSearchResultsPage('CLICK_SEARCH_FILTER_OPTIONS', {
          filter: key,
          event_trigger: contentSearchTrackerStore.eventTrigger,
        })
      }
    },
    [SETrackSearchResultsPage, contentSearchTrackerStore.eventTrigger]
  )

  React.useEffect(() => {
    if (isOpen) {
      setSelectedFormats(contentSearchFilterStore.selectedFormats)
      setSelectedTopics(contentSearchFilterStore.selectedTopics)
    }
  }, [
    contentSearchFilterStore.selectedFormats,
    contentSearchFilterStore.selectedTopics,
    isOpen,
  ])

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      position="bottom"
      sheetSize="xl"
      autoFocus={false}
      returnFocus={false}
    >
      <SheetOverlay />

      <SheetContent className="max-w-xl m-auto">
        <SheetTitle bold>Pilih kategori</SheetTitle>
        <SheetCloseButton onClick={onClose} />
        <div className="p-4 pb-28">
          {Boolean(searchFiltersData?.formats) && (
            <div className="pb-6 mb-6 border-b border-subdued">
              <Text variant="heading-sm" className="mb-4">
                {searchFiltersData?.formats?.header}
              </Text>
              <CheckboxGroup
                name="select-formats"
                className="gap-y-3"
                style={{ flexDirection: 'column' }}
                value={selectedFormats}
                onChange={setSelectedFormats}
              >
                {(searchFiltersData?.formats?.filter ?? []).map(
                  ({ key, label }) => {
                    return (
                      <Checkbox
                        key={`search-filter-format-${key
                          .toLowerCase()
                          .replace(/\s/g, '-')}`}
                        className="!items-center"
                        value={key}
                        onChange={(e) =>
                          clickOptionHandler(e?.target?.checked, key)
                        }
                      >
                        {label}
                      </Checkbox>
                    )
                  }
                )}
              </CheckboxGroup>
            </div>
          )}

          {Boolean(searchFiltersData?.topics) && (
            <div>
              <Text variant="heading-sm" className="mb-4">
                {searchFiltersData?.topics?.header}
              </Text>

              <CheckboxGroup
                name="select-topics"
                className="gap-y-3"
                style={{ flexDirection: 'column' }}
                value={selectedTopics}
                onChange={setSelectedTopics}
              >
                {(searchFiltersData?.topics?.subfilter ?? []).map(
                  (subfilter) => (
                    <>
                      <Text
                        variant="subheading"
                        color="subdued"
                        className="!uppercase"
                      >
                        {subfilter?.title}
                      </Text>

                      {(subfilter?.filter ?? []).map(({ key, label }) => (
                        <Checkbox
                          key={`search-filter-topic-${key
                            .toLowerCase()
                            .replace(/\s/g, '-')}`}
                          className="!items-center"
                          value={key}
                          onChange={(e) =>
                            clickOptionHandler(e?.target?.checked, key)
                          }
                        >
                          {label}
                        </Checkbox>
                      ))}

                      <div className="mb-1 leading-none"></div>
                    </>
                  )
                )}
              </CheckboxGroup>
            </div>
          )}
        </div>
        <div className="absolute bottom-0 w-full p-4 space-y-2 bg-white">
          <Button color="blue" fullWidth onClick={submitFilterHandler}>
            Tampilkan hasil
          </Button>
          <Button
            color="white"
            fullWidth
            disabled={!hasAnyFilterSelected}
            onClick={resetFilterHandler}
          >
            Reset
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default SearchFilterSheet
